<template>
  <LoadingSpinner :loading="loading"> </LoadingSpinner>

  <div class="container mt-4 mb-4">
    <div style="max-width:700px">
    <h3>Send hendelsesvarsel (SMS)</h3>
    <p class="text-muted">
      Varsle kontaktpersonene på utvalgte lokasjoner, f.eks. når en betalingsløsning (Viva, Softpay, Swedbank Pay, Dintero) er nede.
    </p>

    <div class="section">
      <div class="row g-2 align-items-end mb-3">
        <div class="col-sm-6">
          <span class="ingredient-title">Filtrer på betalingsløsning (PSP)</span>
          <select class="form-select" v-model="providerFilter">
            <option value="ALL">Alle</option>
            <option value="VIVA">Viva</option>
            <option value="SOFTPAY">Softpay</option>
            <option value="DINTERO">Dintero</option>
            <option value="SWEDBANK_PAY">Swedbank Pay</option>
          </select>
        </div>
        <div class="col-sm-6">
          <span class="ingredient-title">Søk</span>
          <input class="form-control" type="text" v-model="searchTerm" placeholder="Navn på lokasjon eller merchant" />
        </div>
      </div>

      <div class="mb-2">
        <button type="button" class="btn btn-sm btn-outline-secondary" @click="selectAllFiltered">Velg alle</button>
        <button type="button" class="btn btn-sm btn-outline-secondary ms-2" @click="clearSelection">Fjern alle</button>
        <span class="ms-3 text-muted">{{ selectedLocationIds.length }} valgt</span>
      </div>

      <div v-if="Object.keys(groupedLocations).length === 0" class="text-muted mt-3">
        Ingen lokasjoner matcher filteret.
      </div>

      <div v-for="(locs, merchant) in groupedLocations" :key="merchant" class="mb-3">
        <h6 class="bold">{{ merchant }}</h6>
        <div v-for="loc in locs" :key="loc.id" class="mb-2">
          <label class="multiple-checkbox-label">
            <input type="checkbox" class="multiple-checkbox" :value="loc.id" v-model="selectedLocationIds" />
            {{ loc.name }}
            <span class="text-muted" style="font-size: 0.85rem;">({{ paymentSolutions(loc) }})</span>
          </label>
          <div style="margin-left: 42px; font-size: 0.85rem;">
            <span v-if="contactCount(loc) === 0" class="text-danger">Ingen kontaktpersoner</span>
            <div v-for="(c, ci) in loc.contactPersons" :key="ci" class="text-muted">
              {{ c.name || '(uten navn)' }} – {{ c.phone || '(mangler nummer)' }}<template v-if="c.role"> ({{ c.role }})</template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="section mt-4">
      <span class="ingredient-title">Melding</span>

      <select class="form-select mb-2" v-model="selectedTemplateIndex" @change="applyTemplate">
        <option :value="-1">Velg ferdigskrevet melding</option>
        <option v-for="(t, i) in messageTemplates" :key="i" :value="i">{{ t.label }}</option>
      </select>

      <textarea class="form-control" rows="4" v-model="message"
                placeholder="Skriv informasjonsmelding til kontaktpersonene ..."></textarea>
      <small class="text-muted">Avsender vises som "+4792291541".</small>
    </div>

    <div class="mt-3 mb-5">
      <button type="button" class="bo_btn"
              :disabled="selectedLocationIds.length === 0 || message.trim() === '' || sending"
              @click="send">
        Send SMS
      </button>
    </div>

    <div v-if="results" class="section mt-2 mb-5">
      <h5>Resultat: {{ results.totalSent }} sendt, {{ results.totalFailed }} feilet</h5>
      <div v-for="locResult in results.locations" :key="locResult.locationId" class="mb-3">
        <div class="bold">{{ locResult.locationName || locResult.locationId }}</div>
        <div v-if="locResult.error" class="text-danger" style="font-size: 0.9rem;">
          {{ errorText(locResult.error) }}
        </div>
        <ul class="mb-0">
          <li v-for="(r, i) in locResult.recipients" :key="i">
            {{ r.name || '(uten navn)' }} ({{ r.phone }}):
            <span :class="r.success ? 'text-success' : 'text-danger'">
              {{ r.success ? 'Sendt' : ('Feilet' + (r.error ? ' – ' + errorText(r.error) : '')) }}
            </span>
          </li>
        </ul>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import LoadingSpinner from "@/components/other/LoadingSpinner";
import LocationService from "@/services/location/LocationService";

export default {
  components: { LoadingSpinner },
  data() {
    return {
      loading: true,
      sending: false,
      locations: [],
      selectedLocationIds: [],
      providerFilter: "ALL",
      searchTerm: "",
      message: "",
      selectedTemplateIndex: -1,
      messageTemplates: [
        {
          label: "NETS – kasse og kiosk",
          text: "Det er problem med NETS, og betaling i kasse og selvbetjeningskiosk fungerer ikke. Jeg ser på problemet nå."
        },
        {
          label: "Swedbank – hjemmeside/QR",
          text: "Det er problem med Swedbank online betalingsløsning og betaling via hjemmeside/QR fungerer ikke. Jeg ser på problemet nå."
        }
      ],
      results: null
    };
  },
  computed: {
    filteredLocations() {
      // Only locations that actually use a payment solution (online PSP, Softpay or Viva) are relevant for a payment-outage notice.
      let result = this.locations.filter(l => this.usesAnyPaymentSolution(l));
      if (this.providerFilter !== "ALL") {
        result = result.filter(l => this.matchesProvider(l));
      }
      let term = this.searchTerm.trim().toLowerCase();
      if (term !== "") {
        result = result.filter(l =>
          (l.name || "").toLowerCase().includes(term) ||
          (l.merchant || "").toLowerCase().includes(term)
        );
      }
      return result;
    },
    groupedLocations() {
      const groups = {};
      for (const loc of this.filteredLocations) {
        const m = loc.merchant || "(ukjent merchant)";
        if (!groups[m]) {
          groups[m] = [];
        }
        groups[m].push(loc);
      }
      return groups;
    }
  },
  mounted() {
    LocationService.listAllLocations().then(
      (response) => {
        this.locations = response.data;
        this.loading = false;
      },
      (error) => {
        console.log(error);
        this.loading = false;
      }
    );
  },
  methods: {
    contactCount(loc) {
      return (loc.contactPersons || []).length;
    },
    applyTemplate() {
      if (this.selectedTemplateIndex >= 0) {
        this.message = this.messageTemplates[this.selectedTemplateIndex].text;
      }
    },
    usesAnyPaymentSolution(loc) {
      // A location uses a payment solution if it has an online PSP, or the softpay/viva config exists on the document.
      return (loc.onlinePaymentProvider && loc.onlinePaymentProvider !== "NOT_ANY")
        || loc.softpay != null
        || loc.viva != null;
    },
    matchesProvider(loc) {
      switch (this.providerFilter) {
        case "VIVA": return loc.onlinePaymentProvider === "VIVA" || loc.viva != null;
        case "SOFTPAY": return loc.softpay != null;
        case "DINTERO": return loc.onlinePaymentProvider === "DINTERO";
        case "SWEDBANK_PAY": return loc.onlinePaymentProvider === "SWEDBANK_PAY";
        default: return true;
      }
    },
    paymentSolutions(loc) {
      let solutions = [];
      const add = (s) => { if (solutions.indexOf(s) === -1) solutions.push(s); };
      if (loc.onlinePaymentProvider === "VIVA") add("Viva");
      if (loc.onlinePaymentProvider === "DINTERO") add("Dintero");
      if (loc.onlinePaymentProvider === "SWEDBANK_PAY") add("Swedbank Pay");
      if (loc.softpay != null) add("Softpay");
      if (loc.viva != null) add("Viva");
      return solutions.length > 0 ? solutions.join(", ") : "-";
    },
    selectAllFiltered() {
      this.selectedLocationIds = this.filteredLocations.map(l => l.id);
    },
    clearSelection() {
      this.selectedLocationIds = [];
    },
    errorText(code) {
      switch (code) {
        case "NO_CONTACT_PERSONS": return "Ingen kontaktpersoner registrert";
        case "NO_PHONE": return "Mangler mobilnummer";
        case "LOCATION_NOT_FOUND": return "Fant ikke lokasjonen";
        default: return code;
      }
    },
    send() {
      this.sending = true;
      this.results = null;
      LocationService.sendIncidentNotification(this.selectedLocationIds, this.message).then(
        (response) => {
          this.results = response.data;
          this.sending = false;
        },
        (error) => {
          console.log(error);
          this.sending = false;
        }
      );
    }
  }
};
</script>

<style scoped>
.section {
  background-color: var(--bo-surface-2);
  border: 1px solid var(--bo-line);
  border-radius: 6px;
  padding: 20px;
}
/* Override the global `li { float: left }` from navbar.css so the result list stacks one recipient per line. */
.section ul {
  margin-bottom: 0;
}
.section li {
  float: none;
  list-style: disc;
}
</style>
